import type { JSX } from 'react'
import type { FigureId } from '../../lib/figures'
import { Arrow, FOUR, Glyph, Node, THREE, Words, type Tone } from './kit'

/**
 * The drawings, one per `FigureId`.
 *
 * Schematic on purpose, and never a screenshot. A screenshot goes stale with every rearranged screen,
 * cannot follow a palette or a tenant's brand, and teaches where a control happens to be rather than
 * what it does - which is the same reason the walkthroughs are read alongside the console rather than
 * acted out on top of it. These show the shape of a job, and the shape of a job changes far less
 * often than the pixels of the screen it is done on.
 *
 * Few words inside. What a figure says in text, it says in two or three words under a box; anything
 * longer belongs to the step it sits beside.
 */
type Drawing = (props: { focus: readonly number[] }) => JSX.Element

/** Accent for a one-based position the step is about, plain otherwise. */
function toneAt(focus: readonly number[], position: number): Tone {
  return focus.includes(position) ? 'accent' : 'plain'
}

const Flow: Drawing = ({ focus }) => {
  const steps = [
    { icon: 'book-open', label: 'Dictionary' },
    { icon: 'scroll', label: 'Policy' },
    { icon: 'desktop-tower', label: 'Devices' },
    { icon: 'warning-diamond', label: 'Cases' }
  ] as const
  const verbs = ['binds', 'publish', 'report']

  return (
    <g>
      {steps.map((step, index) => (
        <Node key={step.label} cx={FOUR[index]!} icon={step.icon} label={step.label} tone={toneAt(focus, index + 1)} />
      ))}
      {verbs.map((verb, index) => (
        <Arrow key={verb} x1={FOUR[index]! + 26} y1={42} x2={FOUR[index + 1]! - 26} y2={42} label={verb} />
      ))}
    </g>
  )
}

const DictionaryRule: Drawing = () => (
  <g>
    {/* The rule, as the dialog asks for it: a name, terms, a severity. */}
    <rect className="fig-box" x={10} y={8} width={172} height={104} rx={7} />
    <Words x={22} y={27} kind="label">
      Source code
    </Words>
    {[
      { x: 22, y: 36, width: 70, term: 'BEGIN RSA' },
      { x: 98, y: 36, width: 58, term: 'api_key' },
      { x: 22, y: 60, width: 58, term: 'secret=' }
    ].map((pill) => (
      <g key={pill.term}>
        <rect className="fig-pill" x={pill.x} y={pill.y} width={pill.width} height={18} rx={9} />
        <Words x={pill.x + pill.width / 2} y={pill.y + 12.5} anchor="middle" kind="body">
          {pill.term}
        </Words>
      </g>
    ))}
    <Words x={22} y={100}>
      Severity
    </Words>
    {[0, 1, 2, 3, 4].map((index) => (
      <circle
        key={index}
        className={index < 4 ? 'fig-dot is-on' : 'fig-dot'}
        cx={78 + index * 13}
        cy={96.5}
        r={4.5}
      />
    ))}

    <Arrow x1={188} y1={60} x2={216} y2={60} label="try it" />

    {/* Sample text, as bars, with the one match marked. */}
    <rect className="fig-box" x={222} y={8} width={128} height={104} rx={7} />
    {[
      { y: 24, width: 100 },
      { y: 38, width: 84 },
      { y: 52, width: 104 },
      { y: 66, width: 64 }
    ].map((line) => (
      <rect key={line.y} className="fig-bar" x={234} y={line.y} width={line.width} height={6} rx={3} />
    ))}
    <rect className="fig-mark" x={268} y={34.5} width={42} height={13} rx={3} />
    <Glyph name="check-circle" cx={244} cy={96} size={16} tone="good" />
    <Words x={256} y={100} kind="good">
      1 match
    </Words>
  </g>
)

const RevisionPublish: Drawing = () => (
  <g>
    <Node cx={THREE[0]} icon="file-text" label="Draft" note="you edit" dashed />
    <Arrow x1={THREE[0] + 26} y1={42} x2={THREE[1] - 26} y2={42} label="Publish" />
    <Node
      cx={THREE[1]}
      icon="seal-check"
      label="Revision 1"
      note="frozen"
      tone="accent"
    />
    <Arrow x1={THREE[1] + 26} y1={42} x2={THREE[2] - 26} y2={42} label="Edit" />
    <Node cx={THREE[2]} icon="file-text" label="Draft 2" note="starts fresh" dashed />
  </g>
)

const PolicyAnatomy: Drawing = ({ focus }) => {
  const sections = [
    { icon: 'file-text', label: 'Name' },
    { icon: 'list-checks', label: 'Capability' },
    { icon: 'magnifying-glass', label: 'Looks for' },
    { icon: 'clock', label: 'When' },
    { icon: 'check-circle', label: 'Result' }
  ] as const

  return (
    <g>
      {sections.map((section, index) => {
        const x = 12 + index * 70
        const focused = focus.includes(index + 1)

        return (
          <g key={section.label}>
            {/* A focused section is drawn with a heavier accent rule and no tint, so the number
                inside it stays on the surface its ink was measured against. */}
            <rect className={focused ? 'fig-box is-focus' : 'fig-box'} x={x} y={12} width={56} height={64} rx={7} />
            <Words x={x + 8} y={27} kind={focused ? 'label' : 'note'}>
              {index + 1}
            </Words>
            <Glyph name={section.icon} cx={x + 28} cy={50} size={24} tone={focused ? 'accent' : 'plain'} />
            <Words x={x + 28} y={94} anchor="middle" kind={focused ? 'label' : 'note'}>
              {section.label}
            </Words>
          </g>
        )
      })}
    </g>
  )
}

/**
 * The builder's three questions feeding one policy per host. The fan-out on the right is the part
 * worth drawing: one decision on the left, several policies on the right, held together.
 */
const PolicyBuilder: Drawing = ({ focus }) => (
  <g>
    <Node cx={FOUR[0]} icon="sparkle" label="Assistant" note="Claude" tone={toneAt(focus, 1)} />
    <Arrow x1={FOUR[0] + 26} y1={42} x2={FOUR[1] - 26} y2={42} />
    <Node cx={FOUR[1]} icon="desktop" label="Where" note="browser, app" tone={toneAt(focus, 2)} />
    <Arrow x1={FOUR[1] + 26} y1={42} x2={FOUR[2] - 26} y2={42} />
    <Node cx={FOUR[2]} icon="list-checks" label="What to do" note="flag prompts" tone={toneAt(focus, 3)} />
    <Arrow x1={FOUR[2] + 26} y1={42} x2={FOUR[3] - 26} y2={42} label="one each" />
    <Node cx={FOUR[3]} icon="scroll" label="2 policies" note="one group" tone={toneAt(focus, 4)} />
  </g>
)

const SaveApprovePublish: Drawing = ({ focus }) => (
  <g>
    <Node cx={THREE[0]} icon="file-text" label="Save" note="a revision" tone={toneAt(focus, 1)} />
    <Arrow x1={THREE[0] + 26} y1={42} x2={THREE[1] - 26} y2={42} />
    <Node cx={THREE[1]} icon="user" label="Approve" note="a person read it" tone={toneAt(focus, 2)} />
    <Arrow x1={THREE[1] + 26} y1={42} x2={THREE[2] - 26} y2={42} />
    <Node cx={THREE[2]} icon="desktop-tower" label="Publish" note="every device" tone={toneAt(focus, 3)} />
  </g>
)

const BindByReference: Drawing = () => (
  <g>
    <rect className="fig-box" x={10} y={10} width={160} height={84} rx={7} />
    <Glyph name="scroll" cx={32} cy={32} size={22} />
    <Words x={50} y={36} kind="label">
      Policy
    </Words>
    <rect className="fig-pill" x={22} y={56} width={136} height={24} rx={12} />
    <Glyph name="link-simple" cx={37} cy={68} size={14} tone="accent" />
    <Words x={50} y={72} kind="body">
      Dictionary, rev 3
    </Words>
    <Words x={22} y={111}>
      no terms copied
    </Words>

    <Arrow x1={160} y1={68} x2={274} y2={68} label="resolved at publish" dashed tone="accent" />
    <Node cx={300} cy={60} icon="book-open" label="Dictionary" note="rev 3, published" tone="accent" />
  </g>
)

const KitContents: Drawing = () => {
  const items = [
    { icon: 'package', text: 'Installer (.msi)', tone: 'plain' },
    { icon: 'terminal-window', text: 'install.ps1', tone: 'plain' },
    { icon: 'file-text', text: 'Manifest and policies', tone: 'plain' },
    { icon: 'key', text: 'Enrollment token', tone: 'bad' }
  ] as const

  return (
    <g>
      <Node cx={52} cy={48} icon="file-zip" label="Kit (.zip)" note="downloads once" tone="accent" />
      {items.map((item, index) => {
        const y = 18 + index * 27
        return (
          <g key={item.text}>
            <path className="fig-line is-thin" d={`M 80 48 C 110 48, 118 ${y}, 146 ${y}`} />
            <Glyph name={item.icon} cx={160} cy={y} size={18} tone={item.tone} />
            <Words x={176} y={y + 4} kind={item.tone === 'bad' ? 'bad' : 'body'}>
              {item.text}
            </Words>
          </g>
        )
      })}
      <Words x={176} y={112}>
        a credential: treat it like a password
      </Words>
    </g>
  )
}

const InstallAsUser: Drawing = () => (
  <g>
    <Node
      cx={THREE[0]}
      icon="user"
      label="The person"
      note="at the desk"
      tone="good"
      badge={{ icon: 'check-circle', tone: 'good' }}
    />
    <Node
      cx={THREE[1]}
      icon="user"
      label="Administrator"
      note="wrong profile"
      badge={{ icon: 'x-circle', tone: 'bad' }}
    />
    <Node
      cx={THREE[2]}
      icon="desktop"
      label="SYSTEM"
      note="refused"
      badge={{ icon: 'x-circle', tone: 'bad' }}
    />
  </g>
)

const CheckinWindow: Drawing = () => {
  const polls = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9]

  return (
    <g>
      {/* Row one: the fleet polling. */}
      <Glyph name="arrows-clockwise" cx={20} cy={24} size={16} />
      <Words x={40} y={13}>
        check-in interval
      </Words>
      <line className="fig-line is-thin" x1={40} y1={24} x2={340} y2={24} />
      {polls.map((index) => (
        <circle key={index} className="fig-dot is-on" cx={46 + index * 32} cy={24} r={3.5} />
      ))}

      {/* Row two: how long the credential it polls with lasts. */}
      <Glyph name="key" cx={20} cy={60} size={16} />
      <Words x={40} y={48}>
        credential lifetime
      </Words>
      <rect className="fig-mark" x={40} y={54} width={240} height={12} rx={3} />

      {/* The line a machine that is switched off has to come back inside. */}
      <line className="fig-line is-dashed is-accent" x1={280} y1={44} x2={280} y2={108} />

      {/* Row three: a machine switched off, back in time or not. */}
      <Glyph name="desktop" cx={20} cy={92} size={16} />
      <line className="fig-line is-dashed" x1={40} y1={92} x2={340} y2={92} />
      <Glyph name="check-circle" cx={160} cy={92} size={18} tone="good" />
      <Words x={160} y={114} anchor="middle" kind="good">
        back in time
      </Words>
      <Glyph name="x-circle" cx={312} cy={92} size={18} tone="bad" />
      <Words x={312} y={114} anchor="middle" kind="bad">
        stranded
      </Words>
    </g>
  )
}

const CaseFolding: Drawing = () => {
  const detections = [
    { x: 26, y: 26 },
    { x: 62, y: 18 },
    { x: 44, y: 50 },
    { x: 86, y: 44 },
    { x: 34, y: 80 },
    { x: 74, y: 78 }
  ]

  return (
    <g>
      {detections.map((point) => (
        <g key={`${point.x}-${point.y}`}>
          <path className="fig-line is-thin" d={`M ${point.x + 8} ${point.y} Q 140 ${point.y}, 170 50`} />
          <Glyph name="warning-diamond" cx={point.x} cy={point.y} size={16} />
        </g>
      ))}
      <Words x={60} y={104} anchor="middle">
        6 detections
      </Words>
      <Arrow x1={170} y1={50} x2={252} y2={50} label="fold" />
      <Node cx={278} cy={50} icon="warning-diamond" label="One case" note="count: 6" tone="accent" />
      <Words x={200} y={116} anchor="middle">
        same device, assistant, rules, 15 minutes
      </Words>
    </g>
  )
}

const TrustAnchor: Drawing = () => (
  <g>
    <Node cx={THREE[0]} icon="key" label="Signing anchor" note="this organization" tone="accent" />
    <Arrow x1={THREE[0] + 26} y1={42} x2={THREE[1] - 26} y2={42} label="signs" />
    <Node
      cx={THREE[1]}
      icon="scroll"
      label="Policy"
      note="signed"
      badge={{ icon: 'seal-check', tone: 'good' }}
    />
    <Arrow x1={THREE[1] + 26} y1={42} x2={THREE[2] - 26} y2={42} label="accepts" />
    <Node
      cx={THREE[2]}
      icon="desktop-tower"
      label="Device"
      note="pinned the anchor"
      badge={{ icon: 'shield-check', tone: 'good' }}
    />
  </g>
)

export const DRAWINGS: Record<FigureId, Drawing> = {
  'flow': Flow,
  'dictionary-rule': DictionaryRule,
  'revision-publish': RevisionPublish,
  'policy-anatomy': PolicyAnatomy,
  'policy-builder': PolicyBuilder,
  'save-approve-publish': SaveApprovePublish,
  'bind-by-reference': BindByReference,
  'kit-contents': KitContents,
  'install-as-user': InstallAsUser,
  'checkin-window': CheckinWindow,
  'case-folding': CaseFolding,
  'trust-anchor': TrustAnchor
}
